import React from 'react'
import { FastField, Field, useFormikContext } from 'formik'
import _ from 'lodash'
import { Grid, Typography, Box } from '@mui/material'
import { CustomRadio, CustomRadioGroup, CustomTextField, FormDatePicker } from 'src/form'
import { checkIfKaiserHealthPlan } from 'src/participant/participantUtils'
import BillingCodes from 'src/participantHistory/intakeNote/BillingCodes'
import { DischargeSummaryNoteFormType } from 'src/participantHistoryForm/form/dischargeSummaryNote/DischargeSummaryNoteFormSchema'

const DischargeSummaryNoteSection: React.FC<{ title: string }> = (props) => (
  <Box mt={4}>
    <Typography variant="h2">{props.title}</Typography>
    <Grid container columnSpacing={2.5} rowSpacing={0}>
      {props.children}
    </Grid>
  </Box>
)

const DischargeSummaryNoteSubSection: React.FC<{ title: string; direction?: 'row' | 'column' }> = (props) => (
  <Grid item xs={12} mt={4}>
    <Typography variant="h4">{props.title}</Typography>
    <Box sx={{ display: 'flex', flexDirection: props.direction || 'row', gap: 1 }}>{props.children}</Box>
  </Grid>
)

type DischargeSummaryNoteFieldsProps = {
  viewOnly?: boolean
  credentialingHealthPlan?: string | null
}

const DischargeSummaryNoteFields = ({ viewOnly, credentialingHealthPlan }: DischargeSummaryNoteFieldsProps) => {
  const { values } = useFormikContext<DischargeSummaryNoteFormType>()
  const goalsNotMet = _.get(values, 'treatmentPlan.goalsMet', false) === 'no'
  const memberIdLabel = checkIfKaiserHealthPlan(credentialingHealthPlan) ? 'Kaiser MRN' : 'Insurance member ID'

  return (
    <>
      <DischargeSummaryNoteSection title="Overall Information">
        <DischargeSummaryNoteSubSection title="Participant">
          <Grid item xs={12} md={4}>
            <FastField component={CustomTextField} name="participant.firstName" label="First Name" fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <FastField component={CustomTextField} name="participant.lastName" label="Last Name" fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <FastField component={CustomTextField} name="participant.memberID" label={memberIdLabel} fullWidth />
          </Grid>
        </DischargeSummaryNoteSubSection>

        <DischargeSummaryNoteSubSection title="Assessing Practitioner">
          <Grid item xs={12} md={4}>
            <FastField component={CustomTextField} name="assessingPractitioner.name" label="Name" fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <FastField
              component={CustomTextField}
              name="assessingPractitioner.licenseType"
              label="License Type"
              fullWidth
            />
          </Grid>
        </DischargeSummaryNoteSubSection>

        <DischargeSummaryNoteSubSection title="Discharge Date">
          <Grid item xs={12} md={4}>
            <FastField component={FormDatePicker} name="dischargeDate" label="Discharge Date" fullWidth />
          </Grid>
        </DischargeSummaryNoteSubSection>
      </DischargeSummaryNoteSection>

      <DischargeSummaryNoteSection title="Billing and Diagnosis Codes">
        <Grid item xs={12}>
          <BillingCodes name="billingCodes" viewOnly={viewOnly} />
        </Grid>
      </DischargeSummaryNoteSection>

      <DischargeSummaryNoteSection title="Reason for Discharge">
        <Grid item xs={12}>
          <FastField component={CustomRadioGroup} name="reasonForDischarge.reason" fullWidth>
            <CustomRadio label="Program Completion" value="programCompletion" />
            <CustomRadio label="Early Disenrollment Due to Lack of Program Engagement" value="earlyDisenrollment" />
            <CustomRadio label="Voluntary Program Disenrollment at Pt's Request" value="voluntaryDisenrollment" />
          </FastField>
        </Grid>
        <Grid item xs={12}>
          <FastField
            component={CustomTextField}
            name="reasonForDischarge.details"
            label="Details"
            multiline
            minRows={4}
            fullWidth
            placeholder="Brief note with applicable info on program engagement overall"
          />
        </Grid>
      </DischargeSummaryNoteSection>

      <DischargeSummaryNoteSection title="Progress Assessment">
        <DischargeSummaryNoteSubSection title="SMART Goals:">
          <Grid item xs={12}>
            <FastField
              fullWidth
              component={CustomTextField}
              name="treatmentPlan.smartGoals"
              label="SMART goals"
              multiline
              minRows={4}
            />
          </Grid>
        </DischargeSummaryNoteSubSection>

        <DischargeSummaryNoteSubSection title="Goals Met:" direction="column">
          <Grid item xs={12}>
            <Field component={CustomRadioGroup} name="treatmentPlan.goalsMet" fullWidth>
              <CustomRadio label="Yes" value="yes" />
              <CustomRadio label="No" value="no" />
            </Field>
          </Grid>
          {goalsNotMet && (
            <Grid item xs={12}>
              <FastField
                fullWidth
                component={CustomTextField}
                name="treatmentPlan.details"
                label="Details"
                multiline
                minRows={4}
              />
            </Grid>
          )}
        </DischargeSummaryNoteSubSection>
      </DischargeSummaryNoteSection>
    </>
  )
}

export default DischargeSummaryNoteFields
